import { useEffect, useState } from "react";
import { Undo2, Redo2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { AspectRatio, TimelineAction } from "@scroll/shared-types/timeline";

interface EditorToolbarProps {
  canUndo: boolean;
  canRedo: boolean;
  aspectRatio: AspectRatio;
  isExporting: boolean;
  lastSavedAt: number | null;
  isAutosaving: boolean;
  onDispatch: (action: TimelineAction) => void;
  onClose: () => void;
  onExport: () => void;
}

const ASPECT_RATIOS: AspectRatio[] = ["9:16", "16:9", "1:1", "4:5"];

function formatSavedAgo(timestamp: number, now: number): string {
  const diff = Math.max(0, now - timestamp);
  if (diff < 5_000) return "just now";
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  return `${Math.floor(diff / 3_600_000)}h ago`;
}

export default function EditorToolbar({
  canUndo,
  canRedo,
  aspectRatio,
  isExporting,
  lastSavedAt,
  isAutosaving,
  onDispatch,
  onClose,
  onExport,
}: EditorToolbarProps) {
  // Tick once a minute so the "Saved Xs ago" relative label stays roughly fresh
  // without thrashing rendering. Skips re-rendering until we have a save to show.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (lastSavedAt == null) return;
    const id = window.setInterval(() => setTick((n) => n + 1), 30_000);
    return () => window.clearInterval(id);
  }, [lastSavedAt]);

  const savedLabel = isAutosaving
    ? "Saving…"
    : lastSavedAt != null
      ? `Saved ${formatSavedAgo(lastSavedAt, Date.now())}`
      : null;

  return (
    <header className="timeline-toolbar">
      <div className="timeline-toolbar-group">
        <button
          className="timeline-toolbar-btn"
          onClick={onClose}
          title="Close editor"
          aria-label="Close editor"
        >
          <X size={18} />
        </button>
        <div className="timeline-toolbar-title">
          <span>Timeline editor</span>
          <small>Advanced video edit</small>
        </div>
      </div>

      <div className="timeline-toolbar-group">
        <button
          className="timeline-toolbar-btn"
          onClick={() => onDispatch({ type: "UNDO" })}
          disabled={!canUndo}
          title="Undo (Cmd+Z)"
          aria-label="Undo"
        >
          <Undo2 size={16} />
        </button>
        <button
          className="timeline-toolbar-btn"
          onClick={() => onDispatch({ type: "REDO" })}
          disabled={!canRedo}
          title="Redo (Cmd+Shift+Z)"
          aria-label="Redo"
        >
          <Redo2 size={16} />
        </button>

        <div className="aspect-ratio-picker">
          {ASPECT_RATIOS.map((ar) => (
            <button
              key={ar}
              className={`aspect-ratio-btn ${ar === aspectRatio ? "active" : ""}`}
              onClick={() => onDispatch({ type: "SET_ASPECT_RATIO", aspectRatio: ar })}
              aria-pressed={ar === aspectRatio}
              aria-label={`Set aspect ratio ${ar}`}
            >
              {ar}
            </button>
          ))}
        </div>
      </div>

      <div className="timeline-toolbar-group">
        {savedLabel && (
          <span
            className="timeline-toolbar-saved"
            aria-live="polite"
            title={lastSavedAt ? new Date(lastSavedAt).toLocaleTimeString() : undefined}
          >
            {savedLabel}
          </span>
        )}
        <Button
          className="scroll-btn-primary text-sm"
          onClick={onExport}
          disabled={isExporting}
        >
          {isExporting ? "Saving..." : "Done"}
        </Button>
      </div>
    </header>
  );
}
